import { type OfferCard } from '../store/store-api';
import { legalOf, money, openStatus, priceLabel } from '../store/store-model';
import { type DirectoryPlace, type Mode, type TaxonomyCategory } from './places-api';

/**
 * The Places page's pure rules: what each line of the frame says, from the
 * directory's recorded values and nothing else. Kept apart so each is pinned.
 */

export const MODES: readonly { readonly id: Mode; readonly label: string }[] = [
  { id: 'delivery', label: 'Delivery' },
  { id: 'collection', label: 'Collection' },
  { id: 'inperson', label: 'In person' },
];

export type DepartmentId = 'pharmacy' | 'beauty' | 'vitamins' | 'baby' | 'pet' | 'services';

/**
 * The frame's category bar. Each is a slice of the platform's own taxonomy —
 * by the catalogue's sector where it states one, by the shelf's name where it
 * does not — or, for Services, the bookable kind. A department the taxonomy
 * has nothing for finds nothing, and says so, rather than showing everything.
 */
export const DEPARTMENTS: readonly {
  readonly id: DepartmentId;
  readonly label: string;
  readonly plural: string;
  readonly bg: string;
  readonly match?: (category: TaxonomyCategory) => boolean;
}[] = [
  {
    id: 'pharmacy',
    label: 'Pharmacy',
    plural: 'Pharmacies',
    bg: '#f0ecfa',
    match: (c) => c.sector === 'PHARMACY',
  },
  {
    id: 'beauty',
    label: 'Health & beauty',
    plural: 'Health & beauty',
    bg: '#fdeef0',
    match: (c) => c.sector === 'BEAUTY_WELLNESS' || /beauty|skin ?care|hair ?care|cosmetic/i.test(c.name),
  },
  {
    id: 'vitamins',
    label: 'Vitamins',
    plural: 'Vitamins & supplements',
    bg: '#e9f6ee',
    match: (c) => /vitamin|supplement|mineral/i.test(c.name),
  },
  {
    id: 'baby',
    label: 'Baby & child',
    plural: 'Baby & child',
    bg: '#e7f6fa',
    match: (c) => /\bbab(y|ies)\b|\bchild|\binfant|\bkids?\b/i.test(c.name),
  },
  {
    id: 'pet',
    label: 'Pet care',
    plural: 'Pet care',
    bg: '#fdf2e4',
    match: (c) => c.sector === 'PET_HEALTH' || c.sector === 'VETERINARY' || /\bpets?\b|veterin/i.test(c.name),
  },
  { id: 'services', label: 'Services', plural: 'Health services', bg: '#eeeafb' },
];

/** The frame's eight health-need tiles, matched to the taxonomy by the shelf's name. */
export const NEEDS: readonly { readonly label: string; readonly bg: string; readonly pattern: RegExp }[] = [
  { label: 'Weight loss', bg: '#f0ecfa', pattern: /weight/i },
  { label: 'Hair loss', bg: '#e7f6fa', pattern: /hair loss|hair ?growth/i },
  { label: 'Hay fever & allergies', bg: '#e9f6ee', pattern: /hay ?fever|allerg/i },
  { label: 'Stop smoking', bg: '#fdf2e4', pattern: /smok/i },
  { label: 'Pain relief', bg: '#fdeef0', pattern: /pain/i },
  { label: 'Diabetes', bg: '#eeeafb', pattern: /diabet/i },
  { label: 'Acne', bg: '#e7f6fa', pattern: /\bacne\b/i },
  { label: 'Travel health', bg: '#f0ecfa', pattern: /travel/i },
];

export function departmentCategories(
  taxonomy: readonly TaxonomyCategory[],
  id: DepartmentId,
): readonly string[] | undefined {
  const department = DEPARTMENTS.find((d) => d.id === id);
  if (department?.match === undefined) return undefined;
  return taxonomy.filter(department.match).map((c) => c.id);
}

export function needCategories(
  taxonomy: readonly TaxonomyCategory[],
  pattern: RegExp,
): readonly TaxonomyCategory[] {
  return taxonomy.filter((c) => pattern.test(c.name));
}

/** "0.4 mi" — computed by the platform from the postcode to the shop's premises. */
export function distanceLabel(miles: number | undefined): string | undefined {
  if (miles === undefined) return undefined;
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles).toString()} mi`;
}

export function whereLabel(place: Pick<DirectoryPlace, 'town' | 'postcode'>): string | undefined {
  const parts = [place.town, place.postcode].filter((part): part is string => part !== undefined);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/** The status chip, from published hours. No hours, no chip. */
export function statusChip(place: Pick<DirectoryPlace, 'openingHours'>, now: Date) {
  const status = openStatus(place.openingHours, now);
  if (status === undefined) return undefined;
  return { open: status.open, label: status.open ? 'Open now' : `Closed · ${status.label.toLowerCase()}` };
}

const DELIVERS = new Set(['LOCAL_DELIVERY', 'NATIONAL_DELIVERY', 'DELIVERY']);

/**
 * The fulfilment chip for the chosen mode, in the shop's own terms: its fee,
 * its minimum and its own undertaking where it published one. Never an ETA
 * the platform worked out (D5).
 */
export function fulfilChip(place: DirectoryPlace, mode: Mode): string | undefined {
  if (mode === 'delivery') {
    if (!place.fulfilmentModes.some((m) => DELIVERS.has(m))) return undefined;
    const parts = [place.deliveryEtaLabel !== undefined ? `Delivery ${place.deliveryEtaLabel}` : 'Delivery'];
    if (place.deliveryFromMinor !== undefined) {
      parts.push(place.deliveryFromMinor === 0 ? 'free' : money(place.deliveryFromMinor, place.currency));
    }
    if (place.minimumOrderMinor !== undefined && place.minimumOrderMinor > 0) {
      parts.push(`${money(place.minimumOrderMinor, place.currency)} min.`);
    }
    return parts.join(' · ');
  }
  if (mode === 'collection') {
    if (!place.fulfilmentModes.includes('COLLECTION')) return undefined;
    return place.collectionEtaLabel !== undefined ? `Collection · ${place.collectionEtaLabel}` : 'Collection';
  }
  return place.bookableServices > 0
    ? `${String(place.bookableServices)} bookable ${place.bookableServices === 1 ? 'service' : 'services'}`
    : undefined;
}

export function ratingLabel(place: Pick<DirectoryPlace, 'rating' | 'reviewCount'>): string | undefined {
  if (place.rating === undefined) return undefined;
  return place.rating.toFixed(1);
}

/** A row on the card, linking into the Store's own dialog for that item. */
export function rowOf(
  slug: string,
  offer: OfferCard,
): {
  readonly title: string;
  readonly sub?: string;
  readonly price: string;
  readonly action: string;
  readonly to: string;
} {
  const base = `/storefronts/${slug}`;
  if (offer.kind === 'SERVICE') {
    const minutes = offer.service?.typicalMinutes;
    return {
      title: offer.title,
      sub: minutes !== undefined ? `Service · ${String(minutes)} min` : 'Service',
      price: priceLabel(offer.price),
      action: offer.bookability?.bookable === true ? 'Book' : 'View',
      to: `${base}?${offer.bookability?.bookable === true ? 'book' : 'item'}=${encodeURIComponent(offer.id)}`,
    };
  }
  const legal = legalOf(offer);
  return {
    title: offer.title,
    ...(offer.brandName !== undefined ? { sub: offer.brandName } : {}),
    price: priceLabel(offer.price),
    action: legal === 'POM' ? 'View' : 'Add',
    to: `${base}?item=${encodeURIComponent(offer.id)}`,
  };
}

/** "See all 24 services", or the shop's shelf when it has no services. */
export function seeAllLabel(place: Pick<DirectoryPlace, 'services' | 'products'>): string {
  if (place.services > 0)
    return `See all ${String(place.services)} ${place.services === 1 ? 'service' : 'services'}`;
  return 'Visit the store';
}

/** A stable tile colour per shop (the frame gives each a gradient). */
const TILES = [
  'linear-gradient(145deg,#6d3ffb,#4a22bd)',
  'linear-gradient(145deg,#12866f,#0b5a4a)',
  'linear-gradient(145deg,#5b2ede,#2b1680)',
  'linear-gradient(145deg,#3f9fd6,#1d5fa8)',
  'linear-gradient(145deg,#4a22bd,#17112b)',
  'linear-gradient(145deg,#d17a16,#8f4f06)',
];
export function tileOf(id: string): string {
  let hash = 7;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TILES[hash % TILES.length] as string;
}

/**
 * Places a set of real coordinates on the page's own drawn map, to scale,
 * around the customer's postcode. Equirectangular is exact enough over a few
 * miles; the scale is chosen so the farthest pin sits inside the frame.
 */
export function projectPins(
  centre: { readonly latitude: number; readonly longitude: number },
  points: readonly { readonly id: string; readonly latitude: number; readonly longitude: number }[],
  width: number,
  height: number,
  padding = 48,
): {
  readonly pins: readonly { readonly id: string; readonly x: number; readonly y: number }[];
  readonly pxPerMile: number;
} {
  const milesPerLat = 69.0;
  const milesPerLon = 69.0 * Math.cos((centre.latitude * Math.PI) / 180);
  const offsets = points.map((p) => ({
    id: p.id,
    dx: (p.longitude - centre.longitude) * milesPerLon,
    dy: (p.latitude - centre.latitude) * milesPerLat,
  }));
  const reach = Math.max(0.5, ...offsets.map((o) => Math.max(Math.abs(o.dx), Math.abs(o.dy))));
  const pxPerMile = Math.min((width / 2 - padding) / reach, (height / 2 - padding) / reach);
  return {
    pxPerMile,
    pins: offsets.map((o) => ({
      id: o.id,
      x: width / 2 + o.dx * pxPerMile,
      y: height / 2 - o.dy * pxPerMile,
    })),
  };
}

export function directionsUrl(coordinates: {
  readonly latitude: number;
  readonly longitude: number;
}): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${String(coordinates.latitude)},${String(coordinates.longitude)}`;
}
